import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/0b83497b-e9f1-4fda-b8ac-9d11cf05e23a/images/dcf6447adde4da426f702150f9bbfe00e8dc2813899f0f23a745a17c846434a8.png";

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-[var(--bb-bg)]"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <div className="bb-grid" />
        <div
          className="bb-orb"
          style={{
            width: 600,
            height: 600,
            background: "var(--bb-copper)",
            top: "20%",
            right: "-10%",
          }}
        />
        <div
          className="bb-orb"
          style={{
            width: 480,
            height: 480,
            background: "var(--bb-olive)",
            bottom: "-10%",
            left: "-5%",
            animationDelay: "2s",
          }}
        />
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-[0.28] mix-blend-screen bb-img-accent"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--bb-bg)]" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 pt-[140px] md:pt-[160px] pb-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="bb-tag">
            <span className="dot" />
            <span>PROTOCOLO / 2025</span>
          </span>
          <span className="hidden sm:block mono-label">
            Consultoría IA&nbsp;·&nbsp;Madrid
          </span>
        </motion.div>

        <h1 className="font-display text-[14vw] md:text-[10vw] lg:text-[8.2vw] leading-[0.92] tracking-[-0.035em] text-[var(--bb-text)]">
          <SplitReveal text="Inteligencia" delay={0.2} />
          <br />
          <span className="font-serif-italic text-[var(--bb-copper-light)] inline-block">
            <SplitReveal text="Diseñada." delay={0.35} italic />
          </span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mt-16 md:mt-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5"
          >
            <div className="mono-label mb-4">[ 01 / MANIFIESTO ]</div>
            <p className="text-[var(--bb-text-2)] text-lg leading-relaxed font-body max-w-md">
              Desplegamos arquitecturas de inteligencia artificial de élite
              para resolver problemas empresariales complejos.
              <span className="text-[var(--bb-text)]">
                {" "}
                El poder computacional bruto
              </span>{" "}
              se une a la disciplina estructural.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-6 md:col-start-7 flex flex-col gap-10"
          >
            <div className="font-serif-italic text-2xl md:text-3xl text-[var(--bb-text)] leading-snug">
              “Arquitectado para la Perfección.”
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={onBook}
                className="bb-cta"
                data-testid="hero-cta-primary"
              >
                <span>Agendar Reunión</span>
                <span aria-hidden>↗</span>
              </button>
              <a
                href="#services"
                className="bb-cta-ghost"
                data-testid="hero-cta-secondary"
              >
                <span>Explorar Matriz</span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-10 left-6 md:left-10 flex items-center gap-3"
        >
          <div className="w-10 h-px bg-[var(--bb-text-2)]" />
          <span className="mono-label">Scroll</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-10 right-6 md:right-10 mono-label hidden md:flex items-center gap-2"
        >
          <span>LAT&nbsp;40.4168°N</span>
          <span className="text-[var(--bb-copper)]">·</span>
          <span>LON&nbsp;3.7038°W</span>
        </motion.div>
      </div>
    </section>
  );
};

const SplitReveal = ({ text, delay = 0 }) => {
  return (
    <span className="inline-block">
      {text.split("").map((ch, i) => (
        <span
          key={i}
          style={{ display: "inline-block", overflow: "hidden" }}
          className="bb-char"
        >
          <motion.span
            style={{ display: "inline-block" }}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 1.1,
              delay: delay + i * 0.04,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {ch === " " ? "\u00A0" : ch}
          </motion.span>
        </span>
      ))}
    </span>
  );
};
